import { memo } from 'react';
import styled, { css } from 'styled-components';

import { TEXT_COLOR_BY_BACKGROUND_COLOR } from '@front/common/src/components/label/styles';
import type { LabelCommonProps } from '@front/common/src/components/label/types';
import colors from '@front/common/src/utils/colors';

import type { TextProps } from '../base-text/base-text';
import Text from '../base-text/variants/text';
import Title from '../base-text/variants/title';
import type { WithClassNameProps } from '../types';

type LabelStyleProps = {
  $backgroundColor: string;
};

const labelStyle = css<LabelStyleProps>`
  padding: 2px 4px;
  border-radius: 2px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

const StyledXXSLabel = styled(Text.XXSBold)<LabelStyleProps>`
  ${labelStyle}
`;

const StyledXSLabel = styled(Text.XSBold)<LabelStyleProps>`
  ${labelStyle}
`;

const StyledSMLabel = styled(Text.SMBold)<LabelStyleProps>`
  ${labelStyle}
`;

const StyledMDLabel = styled(Text.MDBold)<LabelStyleProps>`
  ${labelStyle}
`;

const StyledLGLabel = styled(Title.MD)<LabelStyleProps>`
  ${labelStyle}
`;

export type LabelProps = WithClassNameProps & Pick<TextProps, 'children'> & LabelCommonProps;

const Label = ({ backgroundColor = 'ink100', size = 'md', children, className }: LabelProps) => {
  const textColor = colors[TEXT_COLOR_BY_BACKGROUND_COLOR[backgroundColor]];
  const backgroundColorValue = colors[backgroundColor];

  switch (size) {
    case 'lg':
      return (
        <StyledLGLabel $backgroundColor={backgroundColorValue} color={textColor} className={className}>
          {children}
        </StyledLGLabel>
      );
    case 'sm':
      return (
        <StyledSMLabel $backgroundColor={backgroundColorValue} color={textColor} className={className}>
          {children}
        </StyledSMLabel>
      );
    case 'xs':
      return (
        <StyledXSLabel $backgroundColor={backgroundColorValue} color={textColor} className={className}>
          {children}
        </StyledXSLabel>
      );
    case 'xxs':
      return (
        <StyledXXSLabel $backgroundColor={backgroundColorValue} color={textColor} className={className}>
          {children}
        </StyledXXSLabel>
      );
    case 'md':
    default:
      return (
        <StyledMDLabel $backgroundColor={backgroundColorValue} color={textColor} className={className}>
          {children}
        </StyledMDLabel>
      );
  }
};

export default memo(Label);
